import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { serializeDate } from '@adornis/import-export/db/default-serialize-functions.js';
import { DistanceAPI } from '@chancenwerk/address/api/DistanceAPI.js';
import { Adresse } from '@chancenwerk/db/adresse.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import { ExternerMitarbeiter } from '@chancenwerk/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { firstValueFrom } from 'rxjs';

const resolveGenerateTaetigkeitenExport = () => {
  return async () => {
    const allMitarbeiter = (await firstValueFrom(
      UserAPI.subscribeByFilter(
        0,
        5000,
        new ChancenwerkUserFilter({
          userClass: [Mitarbeiter._class, ExternerMitarbeiter._class, VerwaltungsMitarbeiter._class],
        }),
      )({
        _class: 1,
        _id: 1,
        employeeID: 1,
        nachname: 1,
        vorname: 1,
        adresse: Adresse.allFields,
        vertraege: {
          taetigkeitenIDs: 1,
          taetigkeiten: {
            object: { school: { kuerzel: 1, address: Adresse.allFields }, produkt: { kuerzel: 1 } },
            predicate: {
              _class: 1,
              _id: 1,
              name: 1,
              readableName: 1,
            },
            einsaetzeProWoche: 1,
            aufwandsentschaedigung: 1,
            aufwandsentschaedigungTeamtreffen: 1,
            startDate: 1,
            endDate: 1,
          },
        },
      }),
    )) as Mitarbeiter[];

    const distances: Record<string, string> = {};

    // * Try to get all cached distances in bulk
    const ids: string[] = [];
    const froms: string[] = [];
    const tos: string[] = [];
    for (const ma of allMitarbeiter) {
      for (const v of ma.vertraege) {
        for (const t of v.taetigkeiten ?? []) {
          if (!t.object?.school?.address || !ma.adresse) {
            distances[t._id] = '';
            continue;
          }

          ids.push(t._id);
          froms.push(ma.adresse?.toString() ?? '');
          tos.push(t.object?.school?.address?.toString() ?? '');
        }
      }
    }

    const cachedDistances = await DistanceAPI.getDistanceBetweenMultipleCached(froms, tos)();
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      if (!id) {
        continue;
      }

      const res = cachedDistances[i];
      if (res && res.length > 0) {
        distances[id] = res;
      }
    }

    // * Calculate the missing distances
    for (const ma of allMitarbeiter) {
      for (const v of ma.vertraege) {
        for (const t of v.taetigkeiten ?? []) {
          if (distances[t._id] || !t.object?.school?.address || !ma.adresse) {
            continue;
          }

          try {
            const from = ma.adresse?.toString();
            const to = t.object?.school?.address?.toString();

            if (from.length === 0 || to.length === 0) {
              continue;
            }

            // * Get the distance from the API
            const res = await DistanceAPI.getDistanceBetween(from, to)();
            distances[t._id] = res;

            // * Wait a bit if the distance is not cached, to avoid hammering the API
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (e: any) {
            logger.error(e);
          }
        }
      }
    }

    return (
      `Mitarbeiter-ID;Nachname;Vorname;Einsatzort;Einsatztyp;Einsätze pro Woche;Aufwand;Aufwand Teamtreffen;Start;Ende;Entfernung\n` +
      allMitarbeiter
        .flatMap(ma =>
          ma.vertraege.flatMap(v =>
            v.taetigkeiten?.map(t => {
              return [
                ma.employeeID ?? '',
                ma.nachname ?? '',
                ma.vorname ?? '',
                t.object?.kuerzel ?? '',
                t.predicate?.toString() ?? '',
                t.einsaetzeProWoche ?? '',
                t.aufwandsentschaedigung ?? '',
                t.aufwandsentschaedigungTeamtreffen ?? '',
                serializeDate()(t.startDate ?? undefined),
                serializeDate()(t.endDate ?? undefined),
                distances[t._id] ?? '',
              ].join(';');
            }),
          ),
        )
        .reduce((csv, taetigkeitenLine) => (csv += taetigkeitenLine + '\n'), '')
    );
  };
};

export const generateTaetigkeitenExport = registerMutation({
  type: () => String,
  operationName: 'generateTaetigkeitenExport',
  resolve: resolveGenerateTaetigkeitenExport,
});

export namespace ExportAPI {
  export const taetigkeiten = generateTaetigkeitenExport;
}
